<template>
  <div
    class="bp-notify relative flex items-start w-full py-bp-4 px-bp-6 rounded-bp-md shadow-bp-menu pointer-events-auto gap-bp-gap-inline"
    :class="`bp-notify--${colorKey}`"
    :style="notifyStyle"
    role="alert"
    aria-live="polite"
  >
    <span
      class="bp-notify__accent absolute inset-y-0 left-0 rounded-tl-bp-md rounded-bl-bp-md w-[4px]"
      aria-hidden="true"
    ></span>

    <LucideIcon :name="iconName" :size="20" class="bp-notify__icon flex-none mt-bp-1" />

    <div class="bp-notify__body flex-1 min-w-0">
      <h4 v-if="title" class="bp-notify__title m-0 text-bp-sm leading-bp-sm font-bp-semibold">{{ title }}</h4>
      <p
        v-if="text"
        class="bp-notify__text m-0 text-bp-xs leading-bp-xs font-bp-medium opacity-bp-notify-text"
        :class="{ 'mt-bp-1': title }"
      >
        {{ text }}
      </p>
    </div>

    <button
      v-if="closable"
      type="button"
      class="bp-notify__close flex-none p-0 border-0 bg-transparent cursor-pointer opacity-bp-notify-text transition-opacity duration-bp ease-bp hover:opacity-100"
      aria-label="Fechar"
      @click="handleClose"
    >
      <LucideIcon name="x" :size="16" />
    </button>
  </div>
</template>

<script>
import { defineComponent, computed } from 'vue';
import LucideIcon from '../LucideIcon.vue';

const COLORS = ['primary', 'success', 'warning', 'danger', 'dark'];

const ICONS = {
  primary: 'info',
  success: 'check-circle',
  warning: 'alert-triangle',
  danger: 'alert-circle',
  dark: 'info',
};

export default defineComponent({
  name: 'BpNotify',

  components: { LucideIcon },

  emits: ['close'],

  props: {
    title: { type: String, default: '' },
    text: { type: [String, Number, Object], default: '' },

    /** `primary|success|warning|danger|dark` ou cor CSS livre. Espelha o `color` do `$vs.notify`. */
    color: { type: String, default: 'primary' },

    closable: { type: Boolean, default: false },
  },

  setup(props, { emit }) {
    const isNamedColor = computed(() => COLORS.includes(props.color));
    const isFreeColor = computed(() => !isNamedColor.value && !!props.color);

    const colorKey = computed(() => {
      if (isNamedColor.value) return props.color;
      if (isFreeColor.value) return 'custom';
      return 'primary';
    });

    const iconName = computed(() => ICONS[isNamedColor.value ? props.color : 'primary']);

    const notifyStyle = computed(() => {
      if (isFreeColor.value) {
        return {
          '--local-notify-color': props.color,
        };
      }
      return null;
    });

    const handleClose = () => emit('close');

    return { colorKey, iconName, notifyStyle, handleClose };
  },
});
</script>

<style scoped>
.bp-notify {
  max-width: 420px;
  font-family: 'Montserrat', Helvetica, Arial, sans-serif;
}

.bp-notify__text {
  overflow-wrap: anywhere;
}

.bp-notify__close {
  color: inherit;
}

.bp-notify--primary {
  background: theme('colors.bp-primary-soft');
  color: theme('colors.bp-primary-hover');
}
.bp-notify--primary .bp-notify__accent {
  background: theme('colors.bp-primary');
}

.bp-notify--success {
  background: theme('colors.bp-success-soft');
  color: theme('colors.bp-success-hover');
}
.bp-notify--success .bp-notify__accent {
  background: theme('colors.bp-success');
}

.bp-notify--warning {
  background: theme('colors.bp-warning-soft');
  color: theme('colors.bp-warning-hover');
}
.bp-notify--warning .bp-notify__accent {
  background: theme('colors.bp-warning');
}

.bp-notify--danger {
  background: theme('colors.bp-danger-soft');
  color: theme('colors.bp-danger-hover');
}
.bp-notify--danger .bp-notify__accent {
  background: theme('colors.bp-danger');
}

.bp-notify--dark {
  background: theme('colors.bp-dark-soft');
  color: theme('colors.bp-dark');
}
.bp-notify--dark .bp-notify__accent {
  background: theme('colors.bp-dark');
}

.bp-notify--custom {
  background: theme('colors.bp-surface-muted');
  color: var(--local-notify-color);
}
.bp-notify--custom .bp-notify__accent {
  background: var(--local-notify-color);
}
</style>
